import L from 'leaflet'
import { useEffect, useMemo, useRef } from 'react'
import ReactDOMServer from 'react-dom/server'

import { TraceMarkerIcon } from '@/shared/components/MapIcons/TraceMarkerIcon'
import { TruckMarkerIcon } from '@/shared/components/MapIcons/TruckMarkerIcon'

import { HistoricalTraceData, RawTracePoint, TracePoint } from '../models/HistoricalTrace'

type DrawHistoricalTraceArgs = {
  map: L.Map | null
  traces: HistoricalTraceData[]
  activeTraceIndex: number | null
  currentPointIndex: number
  showProcessedPoints: boolean
  showRawPoints: boolean
  showRoute: boolean
}

/**
 * The following hook is used to draw historical trace data on the map widget.
 * It efficiently renders GPS trace points using optimized markers and maintains separate
 * layers for processed points, raw points, routes, and the current position marker.
 * Points are deduplicated to improve performance while maintaining timestamp accuracy.
 * 
 * @param map the Leaflet map object
 * @param traces array of historical trace data to display
 * @param activeTraceIndex index of the currently active trace
 * @param currentPointIndex current position in the trace timeline
 * @param showProcessedPoints whether to display processed GPS points
 * @param showRawPoints whether to display raw GPS points
 * @param showRoute whether to display the route path
 * @returns void
 */
export const useDrawHistoricalTrace = ({
  map,
  traces,
  activeTraceIndex,
  currentPointIndex,
  showProcessedPoints,
  showRawPoints,
  showRoute,
}: DrawHistoricalTraceArgs) => {
  // Refs to store map layers instead of individual markers for better performance
  const processedPointsLayerRef = useRef<L.LayerGroup | null>(null)
  const rawPointsLayerRef = useRef<L.LayerGroup | null>(null)
  const routeLayersRef = useRef<Map<number, L.Polyline>>(new Map())
  const currentMarkerRef = useRef<L.Marker | null>(null)

  // Deduplicate points for better performance while maintaining timeline integrity
  const deduplicatedPoints = useMemo(() => {
    if (activeTraceIndex === null || !traces[activeTraceIndex]) return { processed: [], raw: [] }

    const activeTrace = traces[activeTraceIndex]
    const processedPoints: TracePoint[] = []
    const rawPoints: RawTracePoint[] = []

    // Use a Map to track unique coordinates and their timestamps
    const processedCoordMap = new Map<string, TracePoint>()
    const rawCoordMap = new Map<string, RawTracePoint>()

    // Process trace points (keep one point per unique location, prioritize by timestamp)
    activeTrace.tracePoints.forEach(point => {
      if (!point.x || !point.y) return

      const key = `${point.x.toFixed(5)},${point.y.toFixed(5)}`
      const existingPoint = processedCoordMap.get(key)

      if (!existingPoint || new Date(point.deviceTime) > new Date(existingPoint.deviceTime)) {
        processedCoordMap.set(key, { ...point, uiColor: activeTrace.uiColor })
      }
    })

    // Process raw points (similar deduplication)
    activeTrace.actualTracePoints.forEach(point => {
      if (!point.x || !point.y) return

      const key = `${point.x.toFixed(5)},${point.y.toFixed(5)}`
      const existingPoint = rawCoordMap.get(key)

      if (!existingPoint || new Date(point.deviceTime) > new Date(existingPoint.deviceTime)) {
        rawCoordMap.set(key, { ...point, uiColor: activeTrace.uiColor })
      }
    })

    // Convert maps back to arrays while maintaining chronological order
    processedCoordMap.forEach(point => processedPoints.push(point))
    rawCoordMap.forEach(point => rawPoints.push(point))

    // Sort by timestamp to maintain time sequence
    processedPoints.sort((a, b) =>
      new Date(a.deviceTime).getTime() - new Date(b.deviceTime).getTime(),
    )
    rawPoints.sort((a, b) =>
      new Date(a.deviceTime).getTime() - new Date(b.deviceTime).getTime(),
    )

    return { processed: processedPoints, raw: rawPoints }
  }, [traces, activeTraceIndex])

  // Create/update lightweight SVG icon for trace points
  const createPointIcon = (point: TracePoint | RawTracePoint, isRaw = false) => {
    const iconHtml = ReactDOMServer.renderToStaticMarkup(
      <TraceMarkerIcon
        fillColor={point.uiColor || '#000000'}
        content={isRaw ? 'R' : ''}
        baseOpacity={0.7}
      />,
    )

    return L.divIcon({
      html: iconHtml,
      className: '',
      iconSize: [24, 32],
      iconAnchor: [12, 28],
    })
  }

  // Create/update SVG icon for current position
  const createCurrentPositionIcon = (point: TracePoint) => {
    const iconHtml = ReactDOMServer.renderToStaticMarkup(
      <TruckMarkerIcon
        fillColor={point.uiColor || '#000000'}
      />,
    )

    return L.divIcon({
      html: iconHtml,
      className: '',
      iconSize: [30, 40],
      iconAnchor: [15, 34],
    })
  }

  // Create tooltip HTML for trace points with minimal content for better performance
  const createTooltipHtml = (point: TracePoint | RawTracePoint, isRaw = false) => {
    return `
      <div style="min-width: 180px; max-width: 250px;">
        <div style="font-weight: bold; margin-bottom: 3px;">
          ${isRaw ? 'Raw GPS Point' : 'Processed Point'}
        </div>
        <div>Time: ${new Date(point.deviceTime).toLocaleString()}</div>
        <div>Speed: ${point.speed} km/h</div>
        ${point.address ? `<div>Address: ${point.address}</div>` : ''}
      </div>
    `
  }

  // Draw processed points for active trace using layer group for better performance
  useEffect(() => {
    if (!map || !showProcessedPoints || activeTraceIndex === null) {
      if (processedPointsLayerRef.current) {
        processedPointsLayerRef.current.remove()
        processedPointsLayerRef.current = null
      }

      return
    }

    // Get the current truck position's timestamp
    const activeTrace = traces[activeTraceIndex]

    if (!activeTrace || !activeTrace.tracePoints || activeTrace.tracePoints.length === 0) return

    const currentPoint = activeTrace.tracePoints[currentPointIndex >= activeTrace.tracePoints.length ? activeTrace.tracePoints.length - 1 : currentPointIndex]

    if (!currentPoint) return

    const currentTimestamp = new Date(currentPoint.deviceTime).getTime()

    // Clean up previous layer if it exists
    if (processedPointsLayerRef.current) {
      processedPointsLayerRef.current.remove()
      processedPointsLayerRef.current = null
    }

    // Create a new layer group for all markers
    processedPointsLayerRef.current = L.layerGroup().addTo(map)

    // Filter points by timestamp to exactly match the current truck position time
    const timeFilteredPoints = deduplicatedPoints.processed.filter(point =>
      new Date(point.deviceTime).getTime() <= currentTimestamp,
    )

    for (let i = 0; i < timeFilteredPoints.length; i += 1) {
      const point = timeFilteredPoints[i]

      if (!map || !point.x || !point.y || !processedPointsLayerRef.current) return

      const icon = createPointIcon(point)
      const marker = L.marker([point.y, point.x], {
        icon,
        // Only add higher z-index to the last point which represents the latest position
        zIndexOffset: (i === timeFilteredPoints.length - 1) ? 1000 : 0,
      })

      // Add tooltip only on mouseover for better performance
      marker.bindTooltip(createTooltipHtml(point), {
        permanent: false,
        direction: 'right',
        offset: L.point(10, -15),
      })

      processedPointsLayerRef.current.addLayer(marker)
    }

    return () => {
      if (processedPointsLayerRef.current) {
        processedPointsLayerRef.current.remove()
        processedPointsLayerRef.current = null
      }
    }
  }, [map, deduplicatedPoints.processed, activeTraceIndex, showProcessedPoints, currentPointIndex, traces])

  // Draw raw points for active trace using similar batch processing
  useEffect(() => {
    if (!map || !showRawPoints || activeTraceIndex === null) {
      if (rawPointsLayerRef.current) {
        rawPointsLayerRef.current.remove()
        rawPointsLayerRef.current = null
      }

      return
    }

    // Get the current truck position's timestamp as our reference point
    const activeTrace = traces[activeTraceIndex]

    if (!activeTrace || !activeTrace.tracePoints || activeTrace.tracePoints.length === 0) return

    const currentPoint = activeTrace.tracePoints[currentPointIndex >= activeTrace.tracePoints.length ? activeTrace.tracePoints.length - 1 : currentPointIndex]

    if (!currentPoint) return

    const currentTimestamp = new Date(currentPoint.deviceTime).getTime()

    // Clean up previous layer
    if (rawPointsLayerRef.current) {
      rawPointsLayerRef.current.remove()
      rawPointsLayerRef.current = null
    }

    // Create a new layer group
    rawPointsLayerRef.current = L.layerGroup().addTo(map)

    // Filter raw points by timestamp to match current truck position time
    const timeFilteredRawPoints = deduplicatedPoints.raw.filter(point =>
      new Date(point.deviceTime).getTime() <= currentTimestamp,
    )

    // Process in batches
    const batchSize = 50

    for (let i = 0; i < timeFilteredRawPoints.length; i += batchSize) {
      const batch = timeFilteredRawPoints.slice(i, i + batchSize)

      batch.forEach(point => {
        if (!map || !point.x || !point.y || !rawPointsLayerRef.current) return

        const icon = createPointIcon(point, true)
        const marker = L.marker([point.y, point.x], {
          icon,
          opacity: 0.6, // Lower opacity for raw points
        })

        marker.bindTooltip(createTooltipHtml(point, true), {
          permanent: false,
          direction: 'right',
          offset: L.point(10, -15),
        })

        rawPointsLayerRef.current.addLayer(marker)
      })
    }

    return () => {
      if (rawPointsLayerRef.current) {
        rawPointsLayerRef.current.remove()
        rawPointsLayerRef.current = null
      }
    }
  }, [map, deduplicatedPoints.raw, activeTraceIndex, showRawPoints, currentPointIndex, traces])

  // Draw route for active trace with simplified polyline for better performance
  // Always show the complete route path
  useEffect(() => {
    if (!map || !showRoute || activeTraceIndex === null) {
      // Clear existing routes if not showing
      routeLayersRef.current.forEach(route => {
        route.remove()
      })
      routeLayersRef.current.clear()

      return
    }

    const activeTrace = traces[activeTraceIndex]

    if (!activeTrace || !activeTrace.tracePoints || activeTrace.tracePoints.length === 0) return

    // Clear previous routes for this trace
    const existingRoute = routeLayersRef.current.get(activeTraceIndex)

    if (existingRoute) {
      existingRoute.remove()
      routeLayersRef.current.delete(activeTraceIndex)
    }

    // Always use the complete set of route points
    let routePoints = []

    if (activeTrace.wayPoints?.length > 0) {
      // Use all waypoints if available
      routePoints = activeTrace.wayPoints
    } else {
      // Use all processed points as route points
      routePoints = deduplicatedPoints.processed.map(p => ({ x: p.x, y: p.y }))
    }

    // Simplify the route by removing points that are too close together
    // This improves rendering performance significantly
    const simplifiedPoints = []
    let lastPoint = null

    for (const point of routePoints) {
      if (!point.x || !point.y) continue

      if (!lastPoint ||
        Math.abs(point.x - lastPoint.x) > 0.0001 ||
        Math.abs(point.y - lastPoint.y) > 0.0001) {
        simplifiedPoints.push(point)
        lastPoint = point
      }
    }

    if (simplifiedPoints.length < 2) return // Need at least 2 points to draw a line

    const latlngs = simplifiedPoints.map(point => new L.LatLng(point.y, point.x))
    const route = L.polyline(latlngs, {
      color: activeTrace.uiColor || '#3388ff',
      weight: 4,
      opacity: 0.7,
      smoothFactor: 2, // Higher smooth factor for better performance
    })

    route.addTo(map)
    routeLayersRef.current.set(activeTraceIndex, route)

    return () => {
      if (existingRoute) {
        existingRoute.remove()
      }
    }
  }, [map, traces, activeTraceIndex, showRoute, deduplicatedPoints.processed])

  // Draw current position marker
  useEffect(() => {
    if (!map || activeTraceIndex === null || currentPointIndex < 0) {
      if (currentMarkerRef.current) {
        currentMarkerRef.current.remove()
        currentMarkerRef.current = null
      }

      return
    }

    const activeTrace = traces[activeTraceIndex]

    if (!activeTrace || !activeTrace.tracePoints || currentPointIndex >= activeTrace.tracePoints.length) return

    const currentPoint = activeTrace.tracePoints[currentPointIndex]

    if (!currentPoint || !currentPoint.x || !currentPoint.y) return

    // Remove existing marker
    if (currentMarkerRef.current) {
      currentMarkerRef.current.remove()
      currentMarkerRef.current = null
    }

    // Create new marker
    const icon = createCurrentPositionIcon(currentPoint)
    const marker = L.marker([currentPoint.y, currentPoint.x], {
      icon,
      zIndexOffset: 2000, // Ensure current position is on top
    })

    marker.bindTooltip(`
      <div style="min-width: 180px;">
        <div style="font-weight: bold; margin-bottom: 3px;">
          ${activeTrace.runName}
        </div>
        <div>Time: ${new Date(currentPoint.deviceTime).toLocaleString()}</div>
        <div>Speed: ${currentPoint.speed} km/h</div>
        ${currentPoint.address ? `<div>Address: ${currentPoint.address}</div>` : ''}
      </div>
    `, {
      permanent: false,
      direction: 'right',
      offset: L.point(15, -25),
    })

    marker.addTo(map)
    currentMarkerRef.current = marker

    // Smooth panning to current position with proper animation
    map.panTo([currentPoint.y, currentPoint.x], {
      animate: true,
      duration: 0.25, // Decreased from 0.5 for more responsive animation
      easeLinearity: 0.25, // Adjusted for smoother motion
    })

    return () => {
      if (currentMarkerRef.current) {
        currentMarkerRef.current.remove()
        currentMarkerRef.current = null
      }
    }
  }, [map, traces, activeTraceIndex, currentPointIndex])

  // Cleanup on unmount
  useEffect(() => {
    const routeLayerRefTemp = routeLayersRef.current

    return () => {
      if (processedPointsLayerRef.current) {
        processedPointsLayerRef.current.remove()
      }

      if (rawPointsLayerRef.current) {
        rawPointsLayerRef.current.remove()
      }

      routeLayerRefTemp.forEach(route => {
        route.remove()
      })

      if (currentMarkerRef.current) {
        currentMarkerRef.current.remove()
      }
    }
  }, [])
}